import { computed, makeObservable } from 'mobx';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';

import { EntryData } from 'chaika-frontend-types/frontend-cms';

import { IField } from '@store/models/EntryFields';
import { List } from '@store/abstract/List';

import { EntryVersion } from './EntryVersion';

export interface FiledValueParams<T> {
    updateTime: string;
    value: T;
    field: IField;
}

export class EntryVersionList extends List<EntryVersion, Omit<EntryData, 'inDoor'>> {
    protected identifiableFieldName = 'updateTime';

    constructor(params?: Omit<EntryData, 'inDoor'>[]) {
        super(params);
        makeObservable(this);
    }

    @computed
    public get fieldNames(): string[] {
        const names: string[][] = [];

        this.list.forEach(entryVersion => {
            names.push(entryVersion.fields.fieldNames);
        });

        return uniq(flatten(names));
    }

    public getFieldValues<T>(name: string): FiledValueParams<T>[] {
        return this.values.map(value => ({
            updateTime: value.updateTime,
            value: value.getFieldValue(name),
            field: value.getField(name),
        }));
    }

    public getUniqFieldValues<T>(name: string): FiledValueParams<T>[] {
        const values = this.getFieldValues<T>(name);

        const result = values.reduce((acc, value, index, values) => {
            const prevValue = values[index - 1];

            if (prevValue && value.field && !value.field.isEqual(prevValue.value)) {
                // @ts-ignore
                acc.push(value);
            }

            return acc;
        }, []);

        return [values[0], ...result];
    }

    public getUniqFieldValuesUpdateTime(name: string): string[] {
        const values = this.getUniqFieldValues(name);

        return compact(values.map(value => value?.updateTime));
    }

    protected create(params: Omit<EntryData, 'inDoor'>): EntryVersion {
        const entryVersion = new EntryVersion(params);

        return entryVersion;
    }
}
